import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { useQuickFindItemSupplier, useQuickFindPostQueryItemSupplier } from '@atlassian/search-client';
import { EnlargedSearchResultContent } from '@atlassian/search-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { SearchDialogProduct } from '../../..';
import { messages as commonMessages } from '../../../messages';
import { messages } from '../messages';
import { EMPTY_SEARCH_ITEMS } from '../../product-router/product';
import { QuickFindResultRenderer } from '../quick-find-result-renderer';
import { QuickFindConfluenceTransformer } from './transformers/quick-find-confluence-transformer';
import { PersistRecentSearch } from '../../recent-searches/persist-recent-search';
import { quickFindRecentItemResultMapper } from './mappers/quick-find-recent-item-mapper';
import { quickFindRecentSpaceResultMapper } from './mappers/quick-find-recent-space-mapper';
import { getQuickFindPeopleAndTeamsResultMapper } from './mappers/quick-find-people-and-teams-mapper';
import { quickFindConfluenceSpaceMapper } from './mappers/quick-find-confluence-space-mapper';
import { quickFindConfluenceContentMapper } from './mappers/quick-find-confluence-content-mapper';
import { useUserContext } from '../../../common/user-context';
import { quickFindAtlasContentMapper } from './mappers/quick-find-atlas-content-mapper';
import { useExperiments } from '../../../common/experiment-provider';
import { useQueryParams } from '../../products/confluence/confluence-analytics-query-params';
import { useFeaturesContext } from '../../features-context/features-context';
export var ProductKeys = {
  Confluence: 'confluence',
  Atlas: 'townsquare'
};
export var quickFindSearchResultRenderer = function quickFindSearchResultRenderer(_ref) {
  var searchResult = _ref.searchResult,
    isKeyboardHighlighted = _ref.isKeyboardHighlighted;
  return /*#__PURE__*/React.createElement(PersistRecentSearch, null, /*#__PURE__*/React.createElement(EnlargedSearchResultContent, _extends({}, searchResult, {
    isKeyboardHighlighted: isKeyboardHighlighted,
    icon: /*#__PURE__*/React.isValidElement(searchResult.icon) ? searchResult.icon : /*#__PURE__*/React.createElement(React.Fragment, null)
  })));
};

// QS-3240 omitting props as we move QS to a platformised QF
// TODO: this typing should be cleaned up to be QF specific

export var QuickFindConfluence = function QuickFindConfluence(_ref2) {
  var _features$quickFind;
  var onRetry = _ref2.onRetry,
    linkComponent = _ref2.linkComponent,
    confCloudClient = _ref2.confCloudClient,
    aggAbsoluteUrl = _ref2.aggAbsoluteUrl,
    onPreQueryErrorCallback = _ref2.onPreQueryErrorCallback,
    cloudId = _ref2.cloudId,
    orgId = _ref2.orgId,
    primaryProduct = _ref2.primaryProduct;
  var _useUserContext = useUserContext(),
    user = _useUserContext.user;
  var features = useFeaturesContext();
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var appendAnalyticsQueryParams = useQueryParams();
  var isEnhancedSearchAnalyticsEnabled = fg('cc_page_experiences_enhanced_search_analytics_conf');
  var appendQueryParams = isEnhancedSearchAnalyticsEnabled && primaryProduct === 'confluence' ? appendAnalyticsQueryParams : undefined;
  var preQueryItemSupplier = useQuickFindItemSupplier({
    confCloudClient: confCloudClient,
    aggAbsoluteUrl: aggAbsoluteUrl,
    markErrorAsHandled: onPreQueryErrorCallback,
    itemRenderer: quickFindSearchResultRenderer,
    recentContentResultMapper: quickFindRecentItemResultMapper,
    recentSpaceResultMapper: quickFindRecentSpaceResultMapper,
    userId: user.id,
    cloudId: cloudId
  });

  // This currently isn't being used for results fetching or rendering. It is
  // only being used to create an array of sections which are then used for
  // product registration. After product registration, these scopes are then
  // used to call the experiment endpoint, for this reason they need to be
  // valid scopes for each product.
  var defaultSections = useMemo(function () {
    switch (primaryProduct) {
      case ProductKeys.Confluence:
        return [{
          id: 'confluence.page',
          title: ''
        }];
      case ProductKeys.Atlas:
        return [{
          id: 'townsquare.goal',
          title: ''
        }];
      default:
        return [];
    }
  }, [primaryProduct]);
  var _useExperiments = useExperiments(),
    backendExperiment = _useExperiments.backendExperiment;
  var _ref3 = backendExperiment || {},
    experimentId = _ref3.experimentId,
    shadowExperimentId = _ref3.shadowExperimentId,
    experimentLayers = _ref3.experimentLayers;
  var quickFindPeopleAndTeamsResultMapper = getQuickFindPeopleAndTeamsResultMapper(primaryProduct, orgId || '', cloudId);
  var quickFindPostQueryItemSupplier = useQuickFindPostQueryItemSupplier({
    cloudId: cloudId,
    resultRenderer: quickFindSearchResultRenderer,
    confluenceResultMapper: quickFindConfluenceContentMapper,
    atlasResultMapper: quickFindAtlasContentMapper,
    spaceResultMapper: quickFindConfluenceSpaceMapper,
    peopleAndTeamsResultMapper: quickFindPeopleAndTeamsResultMapper,
    sectionTitle: formatMessage(messages.quick_find_results_section),
    userId: user.id,
    orgId: orgId,
    aggAbsoluteUrl: aggAbsoluteUrl,
    primaryProduct: primaryProduct,
    experimentId: experimentId,
    shadowExperimentId: shadowExperimentId,
    experimentLayers: experimentLayers === null || experimentLayers === void 0 ? void 0 : experimentLayers.map(function (_ref4) {
      var name = _ref4.name,
        layerId = _ref4.layerId,
        shadowId = _ref4.shadowId;
      return {
        name: name,
        layerId: layerId,
        shadowId: shadowId
      };
    })
  });
  var noResultsPostQueryItemSupplier = useCallback(function () {
    return Promise.resolve(EMPTY_SEARCH_ITEMS);
  }, []);
  var postQueryItemSupplier = features !== null && features !== void 0 && (_features$quickFind = features.quickFind) !== null && _features$quickFind !== void 0 && _features$quickFind.postQueryDisabled ? noResultsPostQueryItemSupplier : quickFindPostQueryItemSupplier;

  // TODO: baseUrl is a holdover from QS and not needed in QF
  var generateAdvancedSearchUrl = useCallback(function (query, baseUrl, usage) {
    var url = '';
    var params = new URLSearchParams();
    var trimmedQuery = query.trim();
    if (trimmedQuery) {
      params.append('text', trimmedQuery);
    }
    if (primaryProduct === ProductKeys.Confluence) {
      url = '/wiki/search/';
      if (usage === 'onInputEnterModified') {
        params.append('product', 'confluence');
      }
    } else if (primaryProduct === ProductKeys.Atlas) {
      url = "/o/".concat(orgId, "/search/");
      params.append('cloudId', cloudId);
    }

    // URLSearchParams's toString will do encode special characters, however spaces
    // are encoded as +. We need to replace them with %20 to match existing behaviour.
    url += "?".concat(params.toString());
    url = url.replace(/\+/g, '%20');
    return url;
  }, [cloudId, orgId, primaryProduct]);
  var title = useMemo(function () {
    switch (primaryProduct) {
      case ProductKeys.Confluence:
        return formatMessage(commonMessages.confluence_tab_label);
      case ProductKeys.Atlas:
        return formatMessage(commonMessages.atlas_tab_label);
      default:
        return '';
    }
  }, [formatMessage, primaryProduct]);
  return /*#__PURE__*/React.createElement(SearchDialogProduct, {
    permissionSupplier: function permissionSupplier() {
      return Promise.resolve(defaultSections.map(function (s) {
        return s.id;
      }));
    },
    sections: defaultSections,
    id: primaryProduct,
    title: title,
    order: 0,
    expandedStateInputPlaceholder: formatMessage(messages.quick_find_search_input_placeholder),
    preQueryItemSupplier: preQueryItemSupplier,
    postQueryItemSupplier: postQueryItemSupplier,
    onRetry: onRetry,
    ResultRenderer: QuickFindResultRenderer,
    SearchItemsTransformer: QuickFindConfluenceTransformer,
    isPrefetchingEnabled: true,
    generateAdvancedSearchUrl: generateAdvancedSearchUrl,
    searchFooterLabel: 'Search your organization',
    linkComponent: linkComponent,
    appendQueryParams: appendQueryParams
  });
};